import * as http from '@/services/http';
import { buildProductNameFilter } from '@/utils/purchase-order/reportRowToExpansionFilter';

// AS-10567 phase 1: variant *identity* only.
//
// The by_product expansion used to make a single call against the
// `inventory_replenishment` measure, which runs the forecast/replenishment
// cube and dominates the dialog's open time. Nothing about rendering a
// checkable variant row with a qty input needs that computation, so phase 1
// asks the master-data cube (`Actual Inventory Quantity` — no forecast) for
// identity columns alone. The reorder suggestion arrives separately, in
// phase 2, and merges in via mergeSuggestions.
//
// Deliberately absent: `[inventory_replenishment]`. Adding it here would
// re-introduce the wait this split exists to remove.
export const VARIANT_IDENTITY_COLUMNS = [
  'variant_id',
  'product_id',
  'Item[Item Description]',
  'variant',
  'product_sku',
  'variant_image',
];

// Master Data API returns OLAP format: `rows[].items` is positional and
// aligned with the `columns` metadata array.
interface OlapItem { value?: any; original_value?: any; }
interface OlapRow { items?: OlapItem[]; }

// `value` is display-formatted — a variant_id of 1234567 comes back as
// '1,234,567', which Number() turns into NaN, so asVariantId() would drop
// the variant and it would never reach the purchase list. Prefer
// `original_value`, which carries the raw value.
function itemValue(item: OlapItem): any {
  return item.original_value !== undefined && item.original_value !== null
    ? item.original_value
    : item.value;
}

export function flattenOlapRows(columns: any[], rows: OlapRow[]): any[] {
  const out: any[] = [];
  for (const row of rows || []) {
    const items = row?.items ?? [];
    const flat: any = {};
    items.forEach((item, index) => {
      const meta = columns?.[index];
      if (!meta?.code) { return; }
      flat[meta.code] = itemValue(item);
    });
    out.push(flat);
  }
  return out;
}

// Fetch every variant belonging to the given product names. Returns rows in
// the same flat shape the report expansion produces, so mergeProductExpansion
// consumes them unchanged.
export async function fetchVariantsByProductNames(productNames: string[]): Promise<any[]> {
  if (!productNames || productNames.length === 0) { return []; }

  // operator 'eq' with an array — NOT 'in'.
  //
  // The backend joins multi-value filters with '_or_', splits them again in
  // query_builder.js and converts each value on its own. Its 'in' branch is
  // `"field" IN (${value})` — no quoting, no dataType branch (query_utils.js)
  // — so a text value produces `IN (Blue Shirt)`, which Athena parses as an
  // identifier and rejects. 'in' survives elsewhere in the app only because
  // its callers pass numeric variant_ids.
  //
  // 'eq' + dataType 'text' quotes each value and the conditions are OR-joined,
  // which is exactly IN semantics. buildProductNameFilter is the same filter
  // the existing by_product expansion sends, so this reuses a proven payload
  // rather than a second, subtly different one.
  const productFilter = buildProductNameFilter(productNames);
  if (!productFilter) { return []; }
  const columnFilter = [productFilter];

  const response: any = await http.get('/master-data/variants', {
    column_filter: JSON.stringify(columnFilter),
    columns: VARIANT_IDENTITY_COLUMNS.join(','),
    // A product can carry many variants; 500 matches the ceiling the report
    // expansion already uses for this dialog.
    limit: 500,
  });

  return flattenOlapRows(response?.data?.columns ?? [], response?.data?.rows ?? []);
}
